import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosConfig";

const PAGE_SIZE = 20;

const getMessageId = (message = {}) =>
  String(message?._id || message?.id || message?.tempId || "").trim();

const getFirstNonEmpty = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const normalizeAttachment = (file = {}, fallback = {}) => {
  const fileId = getFirstNonEmpty(file?.fileId, file?.attachmentId, file?._id, file?.id, fallback?.fileId, fallback?.attachmentId);
  const fileName = getFirstNonEmpty(
    file?.name,
    file?.fileName,
    file?.filename,
    file?.originalname,
    fallback?.name,
    fallback?.fileName,
    fallback?.filename
  );
  const fileUrl = getFirstNonEmpty(
    file?.url,
    file?.fileUrl,
    file?.filepathurl,
    file?.path,
    file?.location,
    fallback?.url,
    fallback?.fileUrl,
    fallback?.filepathurl
  );

  if (!fileId && !fileName && !fileUrl) return null;

  return {
    ...file,
    id: file?.id || fileId || undefined,
    _id: file?._id || fileId || undefined,
    fileId: fileId || undefined,
    attachmentId: file?.attachmentId || fileId || undefined,
    name: file?.name || fileName || "Attachment",
    fileName: file?.fileName || fileName || "Attachment",
    filename: file?.filename || fileName || "Attachment",
    url: file?.url || fileUrl || "",
    fileUrl: file?.fileUrl || fileUrl || "",
    filepathurl: file?.filepathurl || fileUrl || "",
  };
};

const normalizeAttachmentsFromMessage = (message = {}) => {
  const fromList = [
    ...(Array.isArray(message?.attachments) ? message.attachments : []),
    ...(Array.isArray(message?.files) ? message.files : []),
    ...(Array.isArray(message?.attachmentPayload) ? message.attachmentPayload : []),
  ]
    .map((item) => normalizeAttachment(item))
    .filter(Boolean);

  if (fromList.length > 0) return fromList;

  const fromUrlList = (Array.isArray(message?.attachmentUrls) ? message.attachmentUrls : [])
    .map((url) =>
      normalizeAttachment(
        {},
        {
          url,
          fileUrl: url,
          name: message?.fileName || message?.filename || message?.messageText || message?.message || "Attachment",
        }
      )
    )
    .filter(Boolean);
  if (fromUrlList.length > 0) return fromUrlList;

  const singleAttachment = normalizeAttachment(
    message?.attachment || message?.file || {},
    {
      fileId: message?.fileId || message?.attachmentId,
      name: message?.fileName || message?.filename || message?.messageText || message?.message,
      url: message?.fileUrl || message?.url || message?.filepathurl || message?.path || message?.location,
    }
  );

  return singleAttachment ? [singleAttachment] : [];
};

const normalizeMessage = (message = {}) => ({
  ...message,
  _id: message?._id || message?.id || message?.tempId,
  message: message?.message || message?.messageText || message?.text || message?.content || "",
  type: message?.type || "text",
  attachments: normalizeAttachmentsFromMessage(message),
  files: normalizeAttachmentsFromMessage(message),
});

const compactPayload = (payload = {}) =>
  Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== ""));

const pushUniqueMessage = (messages = [], incoming = {}) => {
  const normalized = normalizeMessage(incoming);
  const incomingId = getMessageId(normalized);
  const incomingText = String(normalized?.message || "").trim();
  const incomingSenderId = String(normalized?.senderId?._id || normalized?.senderId || "").trim();
  const incomingType = String(normalized?.type || "text").trim().toLowerCase();
  const incomingCreatedAt = String(normalized?.createdAt || "").trim();
  const incomingAttachmentKey = (normalized?.attachments || normalized?.files || [])
    .map((file) => String(file?.fileId || file?.attachmentId || file?.id || file?._id || file?.url || file?.fileUrl || file?.name || "").trim())
    .find(Boolean);

  if (incomingId) {
    const existingIndex = messages.findIndex((msg) => getMessageId(msg) === incomingId);
    if (existingIndex >= 0) {
      messages[existingIndex] = {
        ...messages[existingIndex],
        ...normalized,
        pending: false,
        failed: false,
      };
      return;
    }
  }

  // If server-confirmed message arrives with a different/new id, replace the optimistic pending row.
  const pendingIndex = messages.findIndex((msg) => {
    if (!msg?.pending) return false;
    const pendingText = String(msg?.message || "").trim();
    const pendingSenderId = String(msg?.senderId?._id || msg?.senderId || "").trim();
    const pendingAttachmentKey = (msg?.attachments || msg?.files || [])
      .map((file) => String(file?.fileId || file?.attachmentId || file?.id || file?._id || file?.url || file?.fileUrl || file?.name || "").trim())
      .find(Boolean);
    const isSameTextMessage = pendingText && incomingText && pendingText === incomingText;
    const isSameFileMessage =
      pendingAttachmentKey && incomingAttachmentKey && pendingAttachmentKey === incomingAttachmentKey;
    if (!isSameTextMessage && !isSameFileMessage) return false;
    if (pendingSenderId && incomingSenderId && pendingSenderId !== incomingSenderId) return false;
    return true;
  });
  if (pendingIndex >= 0) {
    messages[pendingIndex] = {
      ...messages[pendingIndex],
      ...normalized,
      pending: false,
      failed: false,
    };
    return;
  }

  // Prevent duplicate persisted messages when same event is received from multiple channels.
  const duplicateConfirmedIndex = messages.findIndex((msg) => {
    if (msg?.pending) return false;
    const existingId = getMessageId(msg);
    if (existingId && incomingId && existingId === incomingId) return true;
    const existingText = String(msg?.message || "").trim();
    const existingSenderId = String(msg?.senderId?._id || msg?.senderId || "").trim();
    const existingType = String(msg?.type || "text").trim().toLowerCase();
    const existingCreatedAt = String(msg?.createdAt || "").trim();
    const existingAttachmentKey = (msg?.attachments || msg?.files || [])
      .map((file) => String(file?.fileId || file?.attachmentId || file?.id || file?._id || file?.url || file?.fileUrl || file?.name || "").trim())
      .find(Boolean);
    const sameSender = existingSenderId && incomingSenderId && existingSenderId === incomingSenderId;
    const sameText = existingText && incomingText && existingText === incomingText;
    const sameType = existingType === incomingType;
    const sameAttachment =
      existingAttachmentKey && incomingAttachmentKey && existingAttachmentKey === incomingAttachmentKey;
    const sameTimestamp = existingCreatedAt && incomingCreatedAt && existingCreatedAt === incomingCreatedAt;
    return sameSender && sameType && sameTimestamp && (sameText || sameAttachment);
  });
  if (duplicateConfirmedIndex >= 0) {
    messages[duplicateConfirmedIndex] = {
      ...messages[duplicateConfirmedIndex],
      ...normalized,
      pending: false,
      failed: false,
    };
    return;
  }

  messages.push(normalized);
};

const mergeOlderMessages = (existing = [], older = []) => {
  const map = new Map();
  [...older, ...existing].forEach((msg) => {
    const normalized = normalizeMessage(msg);
    const id = getMessageId(normalized);
    const key =
      id || `${normalized?.createdAt || ""}_${normalized?.message || ""}_${normalized?.senderId || ""}`;
    if (!map.has(key)) {
      map.set(key, normalized);
    }
  });
  return Array.from(map.values());
};

export const fetchChats = createAsyncThunk("chat/fetchChats", async (params = {}, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get("/chats", { params });
    return Array.isArray(response.data) ? response.data : response.data?.data || [];
  } catch (error) {
    return rejectWithValue(error?.response?.data || "Failed to fetch chats");
  }
});

export const fetchUnreadSummary = createAsyncThunk("chat/fetchUnreadSummary", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get("/messages/unread/summary");
    return response.data?.data || response.data || { totalUnread: 0, byChatId: {} };
  } catch (error) {
    return rejectWithValue(error?.response?.data || "Failed to fetch unread summary");
  }
});

export const markChatAsRead = createAsyncThunk("chat/markChatAsRead", async (chatId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(`/messages/${chatId}/read`);
    return response.data?.data || response.data || { success: true, chatId, modifiedCount: 0 };
  } catch (error) {
    return rejectWithValue(error?.response?.data || "Failed to mark chat as read");
  }
});

export const createChat = createAsyncThunk("chat/createChat", async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post("/chats", payload);
    return response.data?.data || response.data;
  } catch (error) {
    return rejectWithValue(error?.response?.data || "Failed to create chat");
  }
});

export const fetchMessages = createAsyncThunk(
  "chat/fetchMessages",
  async ({ chatId, skip = 0, limit = PAGE_SIZE }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/messages/${chatId}`, { params: { skip, limit } });
      return {
        chatId,
        skip,
        limit,
        messages: Array.isArray(response.data) ? response.data : response.data?.data || [],
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Failed to fetch messages");
    }
  }
);

export const postMessage = createAsyncThunk(
  "chat/postMessage",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/messages", payload);
      return response.data?.data || response.data;
    } catch (error) {
      if (String(payload?.type || "").toLowerCase() === "file") {
        const fallbackPayloads = [
          compactPayload({
            chatId: payload?.chatId,
            messageText: payload?.messageText,
            type: "file",
            fileId: payload?.fileId || payload?.attachmentId,
          }),
          compactPayload({
            chatId: payload?.chatId,
            messageText: payload?.messageText,
            type: "file",
            attachmentId: payload?.attachmentId || payload?.fileId,
          }),
          compactPayload({
            chatId: payload?.chatId,
            messageText: payload?.messageText,
            type: "file",
            attachments: payload?.attachments,
          }),
        ];

        for (const fallbackPayload of fallbackPayloads) {
          try {
            const response = await axiosInstance.post("/messages", fallbackPayload);
            return response.data?.data || response.data;
          } catch {
            // Try the next known backend contract shape.
          }
        }
      }

      return rejectWithValue(error?.response?.data || "Failed to send message");
    }
  }
);

export const uploadChatFile = createAsyncThunk("chat/uploadFile", async (file, { rejectWithValue }) => {
  const uploadWithField = async (fieldName) => {
    const formData = new FormData();
    formData.append(fieldName, file);
    const response = await axiosInstance.post("/messages/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data?.data || response.data;
  };

  try {
    return await uploadWithField("file");
  } catch (error) {
    const message = String(error?.response?.data?.message || error?.response?.data?.error || "").toLowerCase();
    const shouldRetryWithAttachmentField =
      error?.response?.status === 400 || message.includes("unexpected field") || message.includes("file");

    if (shouldRetryWithAttachmentField) {
      try {
        return await uploadWithField("attachment");
      } catch (retryError) {
        return rejectWithValue(retryError?.response?.data || "Failed to upload file");
      }
    }

    return rejectWithValue(error?.response?.data || "Failed to upload file");
  }
});

export const deleteChatMessage = createAsyncThunk("chat/deleteMessage", async (messageId, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`/messages/${messageId}`);
    return { messageId };
  } catch (error) {
    return rejectWithValue(error?.response?.data || "Failed to delete message");
  }
});

export const deleteChat = createAsyncThunk("chat/deleteChat", async (chatId, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`/chats/${chatId}`);
    return { chatId };
  } catch (error) {
    return rejectWithValue(error?.response?.data || "Failed to delete chat");
  }
});

export const lockChat = createAsyncThunk("chat/lockChat", async (chatId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(`/chats/${chatId}/lock`);
    return { chatId, data: response.data?.data || response.data };
  } catch (error) {
    return rejectWithValue(error?.response?.data || "Failed to lock chat");
  }
});

export const unlockChat = createAsyncThunk("chat/unlockChat", async (chatId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(`/chats/${chatId}/unlock`);
    return { chatId, data: response.data?.data || response.data };
  } catch (error) {
    return rejectWithValue(error?.response?.data || "Failed to unlock chat");
  }
});

const initialState = {
  currentRoom: null,
  rooms: [],
  participantIDs: [],
  messages: [],
  chats: [],
  activeChatId: null,
  messagesByChatId: {},
  paginationByChatId: {},
  lockByChatId: {},
  typingByChatId: {},
  chatNotifications: [],
  loadingChats: false,
  loadingMessages: false,
  sendingMessage: false,
  creatingChat: false,
  uploadingFile: false,
  totalUnread: 0,
  error: null,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setRoom(state, action) {
      state.currentRoom = action.payload;
      if (!state.rooms.includes(action.payload)) {
        state.rooms = [...state.rooms, action.payload];
      }
    },
    setRooms(state, action) {
      state.rooms = action.payload;
    },
    setParticipantIDs(state, action) {
      state.participantIDs = action.payload;
    },
    setActiveChat(state, action) {
      state.activeChatId = action.payload || null;
      state.currentRoom = action.payload || null;
      state.messages = state.messagesByChatId[action.payload] || [];
      const activeId = String(action.payload || "");
      if (activeId) {
        const chatIndex = state.chats.findIndex((chat) => String(chat?._id || chat?.id || "") === activeId);
        if (chatIndex >= 0) {
          const previousUnread = Number(state.chats[chatIndex]?.unreadCount || 0);
          state.chats[chatIndex] = {
            ...state.chats[chatIndex],
            unreadCount: 0,
          };
          state.totalUnread = Math.max(Number(state.totalUnread || 0) - previousUnread, 0);
        }
      }
    },
    addMessage(state, action) {
      const message = normalizeMessage(action.payload);
      const chatId = String(message?.chatId || message?.chat?._id || message?.roomId || "").trim();
      if (!chatId) return;

      if (!state.messagesByChatId[chatId]) state.messagesByChatId[chatId] = [];
      pushUniqueMessage(state.messagesByChatId[chatId], message);

      if (state.activeChatId === chatId) {
        state.messages = state.messagesByChatId[chatId];
      }

      const chatIndex = state.chats.findIndex((chat) => String(chat?._id || chat?.id || "") === chatId);
      if (chatIndex >= 0) {
        const shouldIncreaseUnread = String(state.activeChatId || "") !== chatId;
        const currentUnread = Number(state.chats[chatIndex]?.unreadCount || 0);
        state.chats[chatIndex] = {
          ...state.chats[chatIndex],
          lastMessage: message,
          unreadCount: shouldIncreaseUnread ? currentUnread + 1 : 0,
        };
        if (shouldIncreaseUnread) {
          state.totalUnread = Number(state.totalUnread || 0) + 1;
        }
      } else {
        const shouldIncreaseUnread = String(state.activeChatId || "") !== chatId;
        state.chats.unshift({
          _id: chatId,
          name: message?.chatName || "Chat",
          lastMessage: message,
          unreadCount: shouldIncreaseUnread ? 1 : 0,
        });
        if (shouldIncreaseUnread) {
          state.totalUnread = Number(state.totalUnread || 0) + 1;
        }
      }
    },
    addPendingMessage(state, action) {
      const { chatId, tempId, messageText, type = "text", senderId, attachments = [] } = action.payload || {};
      if (!chatId || !tempId) return;
      if (!state.messagesByChatId[chatId]) state.messagesByChatId[chatId] = [];
      pushUniqueMessage(state.messagesByChatId[chatId], {
        chatId,
        tempId,
        _id: tempId,
        message: messageText || "",
        type,
        senderId,
        attachments,
        pending: true,
        createdAt: new Date().toISOString(),
      });
      if (state.activeChatId === chatId) {
        state.messages = state.messagesByChatId[chatId];
      }
    },
    markPendingAsFailed(state, action) {
      const { chatId, tempId } = action.payload || {};
      const list = state.messagesByChatId[chatId] || [];
      const index = list.findIndex((msg) => getMessageId(msg) === String(tempId));
      if (index >= 0) {
        list[index].pending = false;
        list[index].failed = true;
      }
      if (state.activeChatId === chatId) {
        state.messages = list;
      }
    },
    removeMessageById(state, action) {
      const { chatId, messageId } = action.payload || {};
      if (!chatId || !messageId) return;
      const existing = state.messagesByChatId[chatId] || [];
      const next = existing.filter((msg) => getMessageId(msg) !== String(messageId));
      state.messagesByChatId[chatId] = next;
      if (String(state.activeChatId || "") === String(chatId)) {
        state.messages = next;
      }
    },
    applyUnreadSummary(state, action) {
      const payload = action.payload || {};
      const byChatId = payload?.byChatId || {};
      state.totalUnread = Number(payload?.totalUnread || 0);
      state.chats = (state.chats || []).map((chat) => {
        const chatId = String(chat?._id || chat?.id || "");
        return {
          ...chat,
          unreadCount: Number(byChatId?.[chatId] || 0),
        };
      });
    },
    setMessages(state, action) {
      state.messages = action.payload;
      if (state.activeChatId) {
        state.messagesByChatId[state.activeChatId] = action.payload;
      }
    },
    setChatLock(state, action) {
      const { chatId, isLocked } = action.payload || {};
      if (!chatId) return;
      state.lockByChatId[chatId] = Boolean(isLocked);
    },
    setTyping(state, action) {
      const { chatId, user } = action.payload || {};
      if (!chatId) return;
      state.typingByChatId[chatId] = user || null;
    },
    clearTyping(state, action) {
      const chatId = action.payload;
      if (!chatId) return;
      state.typingByChatId[chatId] = null;
    },
    addNotification(state, action) {
      const { roomId, message } = action.payload;
      const exists = state.chatNotifications.find((note) => note.roomId === roomId);
      if (!exists) {
        state.chatNotifications.push({ roomId, message });
      }
    },
    clearNotificationsForRoom(state, action) {
      state.chatNotifications = state.chatNotifications.filter((note) => note.roomId !== action.payload);
    },
    clearChatError(state) {
      state.error = null;
    },
    setChatError(state, action) {
      state.error = action.payload || "Chat error";
    },
    removeChatById(state, action) {
      const chatId = String(action.payload || "");
      state.chats = state.chats.filter((chat) => String(chat?._id || chat?.id || "") !== chatId);
      delete state.messagesByChatId[chatId];
      delete state.paginationByChatId[chatId];
      delete state.lockByChatId[chatId];
      delete state.typingByChatId[chatId];
      if (String(state.activeChatId || "") === chatId) {
        state.activeChatId = null;
        state.currentRoom = null;
        state.messages = [];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChats.pending, (state) => {
        state.loadingChats = true;
      })
      .addCase(fetchChats.fulfilled, (state, action) => {
        state.loadingChats = false;
        state.chats = action.payload;
      })
      .addCase(fetchChats.rejected, (state, action) => {
        state.loadingChats = false;
        state.error = action.payload || "Failed to fetch chats";
      })
      .addCase(fetchUnreadSummary.fulfilled, (state, action) => {
        const byChatId = action.payload?.byChatId || {};
        state.totalUnread = Number(action.payload?.totalUnread || 0);
        state.chats = (state.chats || []).map((chat) => {
          const chatId = String(chat?._id || chat?.id || "");
          return {
            ...chat,
            unreadCount: Number(byChatId?.[chatId] || 0),
          };
        });
      })
      .addCase(markChatAsRead.fulfilled, (state, action) => {
        const chatId = String(action.payload?.chatId || "");
        if (!chatId) return;
        const chatIndex = state.chats.findIndex((chat) => String(chat?._id || chat?.id || "") === chatId);
        if (chatIndex >= 0) {
          state.chats[chatIndex] = {
            ...state.chats[chatIndex],
            unreadCount: 0,
          };
        }
      })
      .addCase(fetchMessages.pending, (state) => {
        state.loadingMessages = true;
      })
      .addCase(createChat.pending, (state) => {
        state.creatingChat = true;
      })
      .addCase(createChat.fulfilled, (state, action) => {
        state.creatingChat = false;
        const chat = action.payload;
        const chatId = String(chat?._id || chat?.id || "");
        if (!chatId) return;
        const existingIndex = state.chats.findIndex((item) => String(item?._id || item?.id || "") === chatId);
        if (existingIndex >= 0) {
          state.chats[existingIndex] = { ...state.chats[existingIndex], ...chat };
        } else {
          state.chats.unshift(chat);
        }
      })
      .addCase(createChat.rejected, (state, action) => {
        state.creatingChat = false;
        state.error = action.payload || "Failed to create chat";
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        const { chatId, messages, skip, limit } = action.payload;
        const existing = state.messagesByChatId[chatId] || [];
        const merged = skip > 0 ? mergeOlderMessages(existing, messages) : mergeOlderMessages([], messages);
        state.messagesByChatId[chatId] = merged;
        state.paginationByChatId[chatId] = {
          skip: skip + limit,
          hasMore: messages.length >= limit,
          limit,
        };
        if (String(state.activeChatId || "") === String(chatId)) {
          state.messages = merged;
        }
        state.loadingMessages = false;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loadingMessages = false;
        state.error = action.payload || "Failed to fetch messages";
      })
      .addCase(postMessage.pending, (state) => {
        state.sendingMessage = true;
      })
      .addCase(postMessage.fulfilled, (state, action) => {
        state.sendingMessage = false;
        const message = normalizeMessage(action.payload);
        const chatId = String(message?.chatId || state.activeChatId || "").trim();
        if (!chatId) return;
        if (!state.messagesByChatId[chatId]) state.messagesByChatId[chatId] = [];
        pushUniqueMessage(state.messagesByChatId[chatId], message);
        if (String(state.activeChatId || "") === chatId) {
          state.messages = state.messagesByChatId[chatId];
        }
      })
      .addCase(postMessage.rejected, (state, action) => {
        state.sendingMessage = false;
        state.error = action.payload || "Failed to send message";
      })
      .addCase(uploadChatFile.pending, (state) => {
        state.uploadingFile = true;
      })
      .addCase(uploadChatFile.fulfilled, (state) => {
        state.uploadingFile = false;
      })
      .addCase(uploadChatFile.rejected, (state, action) => {
        state.uploadingFile = false;
        state.error = action.payload || "Failed to upload file";
      })
      .addCase(lockChat.fulfilled, (state, action) => {
        state.lockByChatId[action.payload.chatId] = true;
      })
      .addCase(unlockChat.fulfilled, (state, action) => {
        state.lockByChatId[action.payload.chatId] = false;
      })
      .addCase(deleteChatMessage.fulfilled, (state, action) => {
        const messageId = String(action.payload?.messageId || "");
        Object.keys(state.messagesByChatId).forEach((chatId) => {
          const existing = state.messagesByChatId[chatId] || [];
          const filtered = existing.filter((msg) => getMessageId(msg) !== messageId);
          state.messagesByChatId[chatId] = filtered;
          if (String(state.activeChatId || "") === String(chatId)) {
            state.messages = filtered;
          }
        });
      })
      .addCase(deleteChat.fulfilled, (state, action) => {
        const chatId = String(action.payload?.chatId || "");
        state.chats = state.chats.filter((chat) => String(chat?._id || chat?.id || "") !== chatId);
        delete state.messagesByChatId[chatId];
        delete state.paginationByChatId[chatId];
        delete state.lockByChatId[chatId];
        delete state.typingByChatId[chatId];
        if (String(state.activeChatId || "") === chatId) {
          state.activeChatId = null;
          state.currentRoom = null;
          state.messages = [];
        }
      });
  },
});

export const {
  setRoom,
  setRooms,
  addMessage,
  setMessages,
  setParticipantIDs,
  addNotification,
  clearNotificationsForRoom,
  setActiveChat,
  addPendingMessage,
  markPendingAsFailed,
  removeMessageById,
  applyUnreadSummary,
  setChatLock,
  setTyping,
  clearTyping,
  clearChatError,
  setChatError,
  removeChatById,
} = chatSlice.actions;

export default chatSlice.reducer;
